
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import { BLoadingComponent } from 'buefy/types/components';

import { sharedState } from '../../../../../framework/state';
import { getResource, getResourcesNew, putResource } from '../../../../../framework/client/resource';
import { PurchaseInvoice } from '../../../../../../common/application/model/PurchaseInvoice';
import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import { getVendorOptions } from '../../../../service/application_options_service';
import { VendorType } from '../../../../../../common/application/model/enums/VendorType';

import PurchaseInvoiceRows from './row/PurchaseInvoiceRows.vue';
import RegistrationNumberMatchSelect from '../../../../fields/RegistrationNumberMatchSelect.vue';

import { PurchaseInvoiceState } from '../../../../../../common/application/model/enums/PurchaseInvoiceState';
import { Finvoice } from '../../../../../../common/application/model/integration/finvoice/Finvoice';
import { getFinvoiceByInvoiceId, getInvoiceRowsTotalPrice } from '../../../../client/purchase_invoice_client';
import { FileMeta } from '../../../../../../common/application/model/FileMeta';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import { RegistrationNumberMatch } from '../../../../../../common/application/model/integration/finvoice/RegistrationNumberMatch';
import { errorToast, successToast } from '../../../../service/toast_service';
import { updateCachedResourceRow } from '../../../../service/query_cache_service';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';
import { formatCurrency } from '../../../../service/helper_service';

@Component({
    methods: { formatCurrency },
    components: {
        PurchaseInvoiceRows,
        RegistrationNumberMatchSelect,
    },
})
export default class ViewPurchaseInvoice extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as PurchaseInvoice,
        purchaseInvoiceStateOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        entryDate: '',
        dueDate: '',
        vendorName: '',
        vendorType: '',
        purchaseInvoiceState: '',
        finvoice: {} as Finvoice,
        registrationNumberMatches: [] as RegistrationNumberMatch[],
        attachments: [] as FileMeta[],
        loadingComponent: {} as BLoadingComponent,
        loading: false,
        invoiceRowsTotalPrice: 0,
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        await this.loadAsyncData();
        this.local.loading = false;
    }

    async setYourReference() {
        if (this.local.loading) {
            return;
        }

        await this.saveResource(false);
    }

    back() {
        this.$router.go(-1);
    }

    edit() {
        this.$router.push(`/purchase-invoice/${this.local.row.id}/edit`);
    }

    fileUrl(attachment: FileMeta): string {
        return `/api/user/netvisor/purchaseinvoice/attachments/${attachment.resourceId}_${attachment.name}`;
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    private async loadAsyncData(): Promise<void> {
        this.showLoading();

        try {
            this.local.purchaseInvoiceStateOptions = getEnumerationOptions(
                'PurchaseInvoiceState',
                Object.keys(PurchaseInvoiceState),
                this,
            );
            this.local.vendorOptions = await getVendorOptions();
            this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
            this.local.row = (await getResource<PurchaseInvoice>(this.resourceType, this.id))!!;

            this.local.entryDate = dateToDbDateString(
                DateTime.fromJSDate(new Date(this.local.row.entryDate), { zone: 'UTC' }).toJSDate(),
            );
            this.local.dueDate = dateToDbDateString(
                DateTime.fromJSDate(new Date(this.local.row.dueDate), { zone: 'UTC' }).toJSDate(),
            );

            this.local.vendorName = getOptionLabel(this.local.row.vendorId || '', this.local.vendorOptions);
            this.local.vendorType = getOptionLabel(this.local.row.vendorType || '', this.local.vendorTypeOptions);
            this.local.purchaseInvoiceState = getOptionLabel(
                this.local.row.purchaseInvoiceState || '',
                this.local.purchaseInvoiceStateOptions,
            );

            if (
                this.shared.admin ||
                this.shared.hasRole(ApplicationUserRole.MANAGER) ||
                this.shared.hasRole(ApplicationUserRole.MANAGER)
            ) {
                const result = await getFinvoiceByInvoiceId(this.id);
                this.local.finvoice = result.finvoice;
                this.local.registrationNumberMatches = result.registrationNumbers.filter(
                    (match) => match.rowNumber === 'invoice',
                );
            }

            if (this.local.row.externalKey) {
                const parameters = new Map<string, string>().set('resourceId', this.id);
                this.local.attachments = await getResourcesNew<FileMeta>(ApplicationResource.FILE_META, -1, parameters);
            }
            this.local.invoiceRowsTotalPrice = await getInvoiceRowsTotalPrice(this.local.row.id);
        } catch (e) {
            console.error('loadAsyncData error', e);
        }

        this.hideLoading();
    }

    private async saveResource(goBack: boolean) {
        this.showLoading();

        try {
            const updatedRow = await putResource<PurchaseInvoice>(this.resourceType, this.id, this.local.row);

            successToast(this, 'message.purchaseInvoiceSaved');
            updateCachedResourceRow<PurchaseInvoice>(updatedRow, this.id, this.resourceType);

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }

        this.hideLoading();
    }

    private showLoading(): void {
        this.local.loadingComponent = this.$buefy.loading.open({});
    }

    private hideLoading(): void {
        this.local.loadingComponent.close();
    }
}
